import { api } from '@goaltree/backend/convex/_generated/api'
import {
  type PlanAnswers,
  fitPlan,
  planRequest,
  planStartTimestamp,
} from '@goaltree/backend/convex/lib/onboardingFlow'
import type { TreeInput } from '@goaltree/backend/convex/nodes'
import type { ConvexReactClient } from 'convex/react'

import { planUsageArgs } from '@/lib/usage-limit'
import { type GoalAnalysis, answeredDetails, useOnboardingStore } from '@/store/onboarding.store'

type Decompose = ReturnType<typeof import('@/hooks/use-onboarding').useOnboarding>['decompose']

// One goal through the planner with the questions-step answers; `text` is the
// goal itself, or one title of a split input.
export async function planGoal(
  decompose: Decompose,
  text: string,
  answers: PlanAnswers,
  locale: string,
  {
    fullText,
    otherGoals,
    part,
    areas,
    seed = true,
  }: {
    fullText?: string
    otherGoals?: string[]
    part?: number
    areas?: string[]
    seed?: boolean
  } = {},
): Promise<TreeInput | null> {
  const { ownTasks, category } = useOnboardingStore.getState()
  const now = planStartTimestamp(answers)
  const { goalText, ...inputs } = planRequest(text, answers, { now, locale })
  const answered = answeredDetails(part)
  const built = await decompose(goalText, {
    ...inputs,
    ...(seed && ownTasks.length > 0 && { seedTasks: ownTasks }),
    ...(answered.length && { details: answered }),
    ...(areas && areas.length > 1 && { areas }),
    ...(fullText && fullText !== text && { fullText }),
    ...(otherGoals?.length && { otherGoals }),
  })
  if (!built) return null
  const tree = fitPlan(built, text, answers, { now, locale })
  return {
    ...tree,
    ...(category && { category }),
    startMode: answers.startMode ?? 'now',
    ...(answers.startMode === 'date' && answers.startDate ? { startDate: answers.startDate } : {}),
  }
}

// One request per goal text; the questions step awaits it before planning.
const pending = new Map<string, Promise<GoalAnalysis>>()

export function analyzeGoal(convex: ConvexReactClient, goalText: string, locale: string) {
  const key = `${locale}\n${goalText}`
  let p = pending.get(key)
  if (!p) {
    p = convex
      .action(api.onboarding.analyzeGoal, { goalText, locale, ...planUsageArgs() })
      .catch(() => ({ goals: [], questions: [] }))
    pending.set(key, p)
  }
  return p
}
